import React, { FC, useEffect } from 'react';

import {
  Text,
  Box,
  Flex,
  Modal,
  Button,
  IconButton,
  ModalBody,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
} from '@chakra-ui/react';
import { FormControl, FormLabel } from '@chakra-ui/form-control';

import { FieldArray, Form, Formik, useFormikContext } from 'formik';

import { HiMinusCircle } from 'react-icons/hi';

import FormikInput from 'components/primitives/FormikInput';
import FormikDatepicker from 'components/primitives/FormikDatepicker';
import FormikSwitch from 'components/primitives/FormikSwitch';
import FormikTextArea from 'components/primitives/FormikTextArea';

import { SiteFilterableSelect } from 'components/modules/SiteFilterableSelect';

import { validationSchema } from './AddPickupModal.utils';
import {
  AddPickupFormValues,
  AddPickupModalViewProps,
  PickupItemFormValues,
} from './AddPickupModal.props';
import { Site, SiteTypeEnum } from 'types/site';
import { CollectionPoint } from 'types/location';

const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
};

const DayOfTheWeekMap: { [key: string]: string } = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
};

const formatPickupDay = (cp?: CollectionPoint): string => {
  if (!cp) return '';
  const days = (cp as any)?.pickupDayOfTheWeek;
  const frequency = (cp as any)?.pickupFrequency as string | undefined;
  const isDaily = frequency === 'Daily';

  let formatted = '';
  if (isDaily) {
    formatted = '';
  } else if (Array.isArray(days)) {
    formatted =
      days.length === 1
        ? days[0]
        : days.map((d: string) => DayOfTheWeekMap[d] ?? d).join(', ');
  } else if (typeof days === 'string') {
    formatted = days;
  }

  let suffix = frequency ? `(${frequency})` : '';
  if (isDaily) suffix = frequency ?? '';
  return `${formatted} ${suffix}`.trim();
};

const newRow = (date?: Date | ''): PickupItemFormValues => ({
  date: date ?? '',
  eventName: '',
  notes: '',
  isFinalPickup: false,
});

type SiteChangeWatcherProps = {
  sites?: Site[];
  collectionPoints: CollectionPoint[];
  onSelectionChange: (
    site: Site | undefined,
    collectionPoint: CollectionPoint | undefined,
  ) => void;
};

const SiteChangeWatcher: FC<SiteChangeWatcherProps> = ({
  sites,
  collectionPoints,
  onSelectionChange,
}) => {
  const { values, setFieldValue } = useFormikContext<AddPickupFormValues>();
  const siteId = values.site;

  useEffect(() => {
    if (!siteId) {
      onSelectionChange(undefined, undefined);
      return;
    }
    const site = sites?.find((s) => s._id === siteId);
    const cp = collectionPoints.find(
      (c: any) => (c.site?._id ?? c.site) === siteId,
    );
    onSelectionChange(site, cp);

    if (site?.siteType === SiteTypeEnum.THIRD_PARTY_WASH) {
      values.items.forEach((_, idx) => {
        setFieldValue(`items.${idx}.isFinalPickup`, false, false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  return null;
};

const AddPickupModalView: FC<AddPickupModalViewProps> = (props) => {
  const {
    isOpen,
    onClose,
    onCreate,
    isCreating,
    sites,
    collectionPoints,
    selectedDay,
  } = props;

  const [selectedSite, setSelectedSite] = React.useState<Site | undefined>();
  const [selectedCollectionPoint, setSelectedCollectionPoint] = React.useState<
    CollectionPoint | undefined
  >();

  useEffect(() => {
    if (!isOpen) {
      setSelectedSite(undefined);
      setSelectedCollectionPoint(undefined);
    }
  }, [isOpen]);

  const initialValues: AddPickupFormValues = {
    site: '',
    items: [newRow(selectedDay)],
  };

  const showFinalToggle =
    !!selectedSite && selectedSite.siteType !== SiteTypeEnum.THIRD_PARTY_WASH;

  const noCollectionPointForSite = !!selectedSite && !selectedCollectionPoint;

  return (
    <Modal
      size="4xl"
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="20px">
        <ModalCloseButton _focus={{ outline: 'none' }} />
        <ModalHeader textAlign="center">Create New Pickup</ModalHeader>
        <ModalBody>
          <Flex direction="column" px={4} pb={6}>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onCreate}>
              {({ values, isValid }) => (
                <Form>
                  <SiteChangeWatcher
                    sites={sites}
                    collectionPoints={collectionPoints}
                    onSelectionChange={(site, cp) => {
                      setSelectedSite(site);
                      setSelectedCollectionPoint(cp);
                    }}
                  />

                  <SiteFilterableSelect
                    values={sites ?? []}
                    label="Select Customer Site"
                    name="site"
                    labelProps={labelProps}
                    isRequired
                  />

                  {noCollectionPointForSite && (
                    <Text mt={2} color="red.500" fontSize="13px">
                      This site has no collection point — contact admin.
                    </Text>
                  )}

                  <Flex gap={5} mt={4}>
                    <FormControl flex={1}>
                      <FormLabel {...labelProps}>
                        Pickup Day Of The Week
                      </FormLabel>
                      <FormikInput
                        name="_pickupDayOfTheWeek"
                        value={formatPickupDay(selectedCollectionPoint)}
                        isReadOnly
                      />
                    </FormControl>
                    <FormControl flex={1}>
                      <FormLabel {...labelProps}>
                        Site Pickup Instructions
                      </FormLabel>
                      <FormikTextArea
                        name="_pickupInstructions"
                        value={selectedSite?.pickupInstructions ?? ''}
                        description="Visible every pickup in app. Edit in customer details."
                        isReadOnly
                      />
                    </FormControl>
                  </Flex>

                  <FieldArray name="items">
                    {({ push, remove }) => (
                      <>
                        {values.items.map((_item, index) => (
                          <Flex
                            key={index}
                            mt={5}
                            gap={4}
                            alignItems="flex-end">
                            <FormikDatepicker
                              name={`items.${index}.date`}
                              label="Pickup Date"
                              labelProps={labelProps}
                              containerProps={{ flex: 1 }}
                            />
                            <FormikInput
                              name={`items.${index}.eventName`}
                              label="Event Name"
                              labelProps={labelProps}
                              containerProps={{ flex: 1 }}
                            />
                            <FormikInput
                              name={`items.${index}.notes`}
                              label="Pickup Notes"
                              labelProps={labelProps}
                              containerProps={{ flex: 2 }}
                            />
                            {showFinalToggle && (
                              <Flex
                                direction="column"
                                alignItems="center"
                                pb={2}>
                                <Text fontSize="12px" color="#afafaf">
                                  Final/Unused
                                </Text>
                                <Box mt={1}>
                                  <FormikSwitch
                                    name={`items.${index}.isFinalPickup`}
                                  />
                                </Box>
                              </Flex>
                            )}
                            <IconButton
                              aria-label="remove-pickup"
                              icon={
                                <HiMinusCircle size="25px" fill="#E54444" />
                              }
                              size="sm"
                              variant="ghost"
                              isDisabled={values.items.length <= 1}
                              onClick={() => remove(index)}
                              mb={1}
                            />
                          </Flex>
                        ))}

                        <Flex mt={6}>
                          <Button
                            w="100%"
                            size="lg"
                            borderRadius="5px"
                            variant="outline"
                            bg="primary.900"
                            isDisabled={values.items.length >= 10}
                            onClick={() => push(newRow())}>
                            Add Pickup (max 10)
                          </Button>
                        </Flex>
                      </>
                    )}
                  </FieldArray>

                  <Flex mt={10} justifyContent="center">
                    <Button
                      mr="10px"
                      isDisabled={isCreating}
                      w="134px"
                      onClick={onClose}
                      variant="outline">
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      isLoading={isCreating}
                      isDisabled={
                        !isValid ||
                        !values.site ||
                        noCollectionPointForSite ||
                        values.items.length === 0
                      }
                      w="134px">
                      Create
                    </Button>
                  </Flex>
                </Form>
              )}
            </Formik>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AddPickupModalView;
